import { useCallback, useEffect, useRef, useState } from 'react'
import {
  purchaseSubscription,
  createPaymentIntent,
  postPaymentId,
  getPaymentStatus
} from 'services/Api/SubscriptionApi'
import {
  trackSubscriptionPlanSelected,
  trackSubscriptionPlanAdded,
  trackAddSubscriptionFailed
} from './subscriptionEvents'

const SUBSCRIPTION_ADDED = 0
const PAYMENT_NEEDED = 1

export const PAYMENT_STATUS_SUCCESS = 'SUCCESS'
export const PAYMENT_STATUS_FAILED = 'FAILED'
export const PAYMENT_STATUS_PENDING = 'PENDING'
const POLLING_INTERVAL = 3000

// Broadcast on a successful subscription payment so every mounted subscription view (the
// My Plan page AND the expiry banner) re-fetches — paying from one must refresh the other.
export const SUBSCRIPTION_UPDATED_EVENT = 'okc:subscription-updated'

export interface CheckoutPlan {
  id: string
  amount: number
  discount?: number
  name: string
}

export interface UsePlanCheckoutReturn {
  payOpen: boolean
  payLoading: boolean
  payError: string | null
  qrCodeUrl: string | null
  paymentStatus: string | null
  planName: string
  payableRupees: number
  isComplete: boolean
  isFailed: boolean
  startPayment: (plan: CheckoutPlan) => void
  closePayment: () => void
}

// Shared subscription checkout: purchase → UPI payment intent (QR) → status poll.
// Used by the My Plan page CTA and the expiry banner's "Pay to Renew" button so the
// payment popup can open in place from either spot.
export const usePlanCheckout = (
  onSuccessClose?: () => void,
  source = 'my_plan'
): UsePlanCheckoutReturn => {
  const [plan, setPlan] = useState<CheckoutPlan | null>(null)
  const [payOpen, setPayOpen] = useState(false)
  const [payLoading, setPayLoading] = useState(false)
  const [payError, setPayError] = useState<string | null>(null)
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null)
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null)

  const pollingRef = useRef<NodeJS.Timeout | null>(null)
  const isMountedRef = useRef(true)

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }, [])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      stopPolling()
    }
  }, [stopPolling])

  // Fire the success / failure events once the payment settles (covers both the
  // immediate SUBSCRIPTION_ADDED path and the polled UPI result).
  useEffect(() => {
    if (paymentStatus === PAYMENT_STATUS_SUCCESS && plan) {
      trackSubscriptionPlanAdded(plan.id, plan.name, source)
      // Tell every other subscription view to refresh (e.g. paying from the banner must
      // also update the My Plan page, which has its own copy of the subscription).
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(SUBSCRIPTION_UPDATED_EVENT))
      }
    } else if (paymentStatus === PAYMENT_STATUS_FAILED && plan) {
      trackAddSubscriptionFailed('payment_failed', source)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStatus])

  useEffect(() => {
    if (payError && plan) {
      trackAddSubscriptionFailed(payError, source)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payError])

  const startPolling = useCallback(
    (paymentId: string) => {
      const poll = async () => {
        if (!isMountedRef.current) {
          return
        }
        try {
          const res = await getPaymentStatus(paymentId)
          const status = res.data.status
          if (!isMountedRef.current) {
            return
          }
          setPaymentStatus(status)
          if (status !== PAYMENT_STATUS_PENDING) {
            stopPolling()
          }
        } catch (e) {
          console.error('Error polling payment status:', e)
        }
      }
      pollingRef.current = setInterval(poll, POLLING_INTERVAL)
      poll()
    },
    [stopPolling]
  )

  const startPayment = useCallback(
    async (checkoutPlan: CheckoutPlan) => {
      setPlan(checkoutPlan)
      trackSubscriptionPlanSelected(checkoutPlan.id, checkoutPlan.name, source)
      setPayOpen(true)
      setPayLoading(true)
      setPayError(null)
      setQrCodeUrl(null)
      setPaymentStatus(null)
      try {
        const purchaseRes = await purchaseSubscription(checkoutPlan.id, {
          app_source: 'desktop_web'
        })
        const purchaseData = purchaseRes.data
        if (purchaseData.type === SUBSCRIPTION_ADDED) {
          if (isMountedRef.current) {
            setPayLoading(false)
            setPaymentStatus(PAYMENT_STATUS_SUCCESS)
          }
          return
        }
        if (purchaseData.type === PAYMENT_NEEDED && purchaseData.payment_detail) {
          const { link_id, subscription_payment_id } = purchaseData.payment_detail
          const payable = checkoutPlan.amount - (checkoutPlan.discount || 0)
          const intentRes = await createPaymentIntent(
            payable,
            link_id,
            'upi',
            subscription_payment_id
          )
          const qrUrl = intentRes.data.payment_intent?.url
          if (!qrUrl) {
            throw new Error('Payment intent URL not found')
          }
          await postPaymentId(intentRes.data.payment_id, subscription_payment_id)
          if (!isMountedRef.current) {
            return
          }
          setPayLoading(false)
          setQrCodeUrl(qrUrl)
          setPaymentStatus(PAYMENT_STATUS_PENDING)
          startPolling(intentRes.data.payment_id)
        } else {
          throw new Error('Unexpected purchase response')
        }
      } catch (e: any) {
        if (isMountedRef.current) {
          setPayLoading(false)
          setPayError(e?.message || 'Failed to initiate payment')
        }
      }
    },
    [startPolling, source]
  )

  const closePayment = useCallback(() => {
    stopPolling()
    const wasSuccess = paymentStatus === PAYMENT_STATUS_SUCCESS
    setPayOpen(false)
    setQrCodeUrl(null)
    setPayError(null)
    setPaymentStatus(null)
    setPlan(null)
    if (wasSuccess && onSuccessClose) {
      onSuccessClose()
    }
  }, [stopPolling, paymentStatus, onSuccessClose])

  const payableRupees = plan ? Math.round((plan.amount - (plan.discount || 0)) / 100) : 0

  return {
    payOpen,
    payLoading,
    payError,
    qrCodeUrl,
    paymentStatus,
    planName: plan?.name || '',
    payableRupees,
    isComplete: paymentStatus === PAYMENT_STATUS_SUCCESS,
    isFailed: paymentStatus === PAYMENT_STATUS_FAILED,
    startPayment,
    closePayment
  }
}
